import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CurrencyInput from "react-currency-input-field";

import {
  newAllocationPayload,
  updateAllocationStrategy,
} from "features/Dashboard/utils";
import {
  AllocationAmountModalContainer,
  BrokerContainer,
} from "./AllocationAmountModal.styles";

import { toast } from "Layout/slice";
import { Loader } from "components/Loader";
import { Button } from "components/Button";
import { Avatar } from "components/Avatar/index";
import { IconBackArrow } from "assets/icons/index";
import {
  isEmptyObj,
  parseErrorMessage,
  resolveMinimumInvestment,
  roundOff,
} from "utils";
import { getTradingConnectionInfo } from "features/Strategy/service";
import { setShowFeatureDetailsKey, setWalkthrough } from "Layout/slice/index";
import { welcomeWalkthroughSelect } from "Layout/slice/selector";

export const AllocationAmountModal = ({
  isEditAllocation,
  showBackButton = true,
  isEditStrategies = false,
  ...props
}) => {
  const dispatch = useDispatch();
  const isWelcomeWalkthrough = useSelector(welcomeWalkthroughSelect);

  const [errorMessage, setErrorMessage] = useState("");
  const [customAllocationAmount, setCustomAllocationAmount] = useState(100);
  const [allocationError, setAllocationError] = useState("");
  const [strategies, setStrategies] = useState([]);
  const [updatedStrategies, setUpdatedStrategies] = useState([]);
  const [accountValue, setAccountValue] = useState(0);
  const [availableFunds, setAvailableFunds] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isWelcomeWalkthrough && !isLoading) {
      setTimeout(() => {
        dispatch(setShowFeatureDetailsKey("allocation"));
        dispatch(setWalkthrough({ run: true, stepIndex: 0, scrollTo: false }));
      }, 500);
    }
  }, [isLoading]);

  useEffect(() => {
    (async () => {
      if (!props.accountId) {
        return;
      }
      try {
        setIsLoading(true);

        const responseTc = await getTradingConnectionInfo(props.accountId);

        if (!isEmptyObj(responseTc)) {
          setStrategies(responseTc?.trading_connections);
          setAccountValue(responseTc?.account_value);
          setAvailableFunds(responseTc?.available_funds_for_trading);
          if (isEditAllocation) {
            setCustomAllocationAmount(responseTc?.total_cash.raw_number);
          } else {
            setCustomAllocationAmount(
              responseTc?.available_funds_for_trading.raw_number
            );
          }
          setAllocationError(responseTc?.message);
        } else {
          props.onHide();
          dispatch(
            toast({
              type: "error",
              body: "No funds available!",
            })
          );
        }
      } catch (error) {
        dispatch(
          toast({
            type: "error",
            body: parseErrorMessage(error),
          })
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [props.accountId]);

  const validateSingleAllocationAmount = (value, minimumInvestment) => {
    let isValid = true;
    const { raw: minRawNumber, formatted: minFormatted } =
      resolveMinimumInvestment(minimumInvestment);

    if (value < minRawNumber) {
      setErrorMessage(`Please enter a value ≥ ${minFormatted}`);
      isValid = false;
    } else if (value > availableFunds?.raw_number) {
      setErrorMessage(
        `Please enter a number less than or equal to ${
          availableFunds?.formatted || "-"
        }`
      );
      isValid = false;
    } else {
      setErrorMessage("");
    }

    return isValid;
  };

  const validateAllStrategiesAllocationAmount = () => {
    let isValid = true;

    for (let i = 0; i < strategies.length; i++) {
      const strategy = strategies[i];
      const isValidAmt = validateSingleAllocationAmount(
        strategy.current_allocation.dollars.raw_number,
        strategy.minimum_investment
      );

      if (!isValidAmt) {
        isValid = isValidAmt;
        break;
      }
    }

    return isValid;
  };

  const handleContinue = async () => {
    try {
      let isValidAmount = true;

      if (isEditStrategies) {
        isValidAmount = validateAllStrategiesAllocationAmount();
      } else {
        isValidAmount = validateSingleAllocationAmount(
          customAllocationAmount,
          props.minimumInvestment
        );
      }

      if (!isValidAmount) return;

      setIsSubmitting(true);

      if (isEditStrategies) {
        const newAllocation = newAllocationPayload(
          strategies,
          accountValue?.raw_number
        );
        const updatedAllocation = newAllocationPayload(
          updatedStrategies,
          accountValue?.raw_number
        );
        const payload = {
          new_allocation: newAllocation.new_allocation,
          update_flag: updatedAllocation.update_flag,
        };
        await props.onContinue(payload);
      } else {
        await props.onContinue(
          customAllocationAmount,
          accountValue?.raw_number
        );
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAllocationAmountChange = (value) => {
    setCustomAllocationAmount(value);
    validateSingleAllocationAmount(value, props.minimumInvestment);
  };

  const handleOtherStratChange = (strategyIdx, value) => {
    validateSingleAllocationAmount(
      value,
      strategies[strategyIdx]?.minimum_investment
    );

    const allStrategies = updateAllocationStrategy(
      { value, strategyIdx },
      strategies,
      accountValue
    );
    setStrategies(allStrategies);

    // Only Updated Strategies
    const strategy = allStrategies[strategyIdx];
    const stratIdx = updatedStrategies.findIndex(
      (st) => st.strategy_id === strategy.strategy_id
    );
    if (stratIdx > -1) {
      updatedStrategies[stratIdx] = strategy;
    } else {
      updatedStrategies.push(strategy);
    }
    setUpdatedStrategies(updatedStrategies);
  };

  const calcVolume = () => {
    if (customAllocationAmount && accountValue?.raw_number) {
      let val = (customAllocationAmount * 100) / accountValue?.raw_number;
      return roundOff(val, 2);
    }
    return 0;
  };

  const getLabel = () => {
    if (isEditAllocation && !isSubmitting) {
      return "Update";
    } else if (!isLoading && !isSubmitting) {
      return "Continue";
    }
  };

  return (
    <AllocationAmountModalContainer
      id="allocation_modal"
      heading=""
      show={props.show}
      onHide={props.onHide}
      className="allocation-amount-modal"
      showClose={props.showClose}
    >
      <div className="heading">
        {showBackButton && (
          <IconBackArrow onClick={props.handleBack} className="back-btn" />
        )}
        <h2>
          {isEditAllocation ? "Change" : "Set"} Allocation Amount{" "}
          {/* <TooltipIcon
            hideStrategiesTip={
              isEditAllocation && !Object.entries(strategies)?.length
            }
          /> */}
        </h2>
      </div>
      <div className="portfolio-wrapper">
        <BrokerContainer>
          {props.portfolioLogo && (
            <Avatar
              size={35}
              round={true}
              name={props?.portfolioName}
              className="broker-img"
              src={props.portfolioLogo}
            />
          )}
          <div className="broker-content">
            <div className="broker-title text-ellipsis">
              {props?.portfolioName}
            </div>
            {props?.portfolioKey && (
              <div className="broker-type">{props?.portfolioKey}</div>
            )}
          </div>
        </BrokerContainer>
      </div>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="spacing-wrapper">
          <div className="account">
            <div className="account-item">
              <h5>Account Value</h5>
              <span>{accountValue?.formatted || "-"}</span>
            </div>
            <div className="account-item">
              <h5>Available Funds</h5>
              <span>{availableFunds?.formatted || "-"}</span>
            </div>
          </div>

          {strategies?.length > 0 && !isLoading ? (
            <h5 className="other">
              Other strategies connected to this account
            </h5>
          ) : (
            <p className="other text-center">
              No other strategies connected to this account
            </p>
          )}

          {strategies.map((strategy, inx) => {
            const dollars = strategy.current_allocation.dollars.raw_number;

            return (
              <div key={inx} className="input-wrapper pb-2">
                <div>
                  <div className="strategy-title">{strategy.strategy_name}</div>
                  <div className="min-value">
                    Min Allocation Value:{" "}
                    {
                      resolveMinimumInvestment(strategy.minimum_investment)
                        .formatted
                    }
                  </div>
                </div>
                <div>
                  {!isEditStrategies && (
                    <div className="d-flex justify-content-center">
                      <div className="pct">
                        {strategy.current_allocation.dollars.formatted}
                      </div>
                      <span className="pct-sub">
                        = {strategy.current_allocation.percent}{" "}
                      </span>
                    </div>
                  )}
                  {isEditStrategies && (
                    <>
                      <div className="input-wrapper amount-input">
                        <div className="negative-margin">$</div>
                        <CurrencyInput
                          id="allocation-price"
                          name="allocation-price"
                          defaultValue={dollars}
                          decimalsLimit={2}
                          decimalScale={2}
                          className="allocation-price-input"
                          value={dollars}
                          allowDecimals={true}
                          onValueChange={(value) =>
                            handleOtherStratChange(inx, value)
                          }
                        />

                        <div className="percentage">
                          ={" " + strategy.current_allocation?.percent}
                        </div>
                      </div>
                      {!isEditAllocation && errorMessage && (
                        <div className="error-msg">{errorMessage}</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          <div className="divider"></div>
          {isEditAllocation && errorMessage && (
            <div className="error-msg">{errorMessage}</div>
          )}
          {props.strategyName && (
            <>
              <div className="input-wrapper ">
                <div>
                  <div className="strategy-title">{props.strategyName}</div>
                  <div className="min-value">
                    Min Allocation Value:{" "}
                    {
                      resolveMinimumInvestment(props.minimumInvestment)
                        .formatted
                    }
                  </div>
                </div>
                <div className="d-flex justify-content-center align-items-center">
                  <div className="negative-margin">$</div>
                  <CurrencyInput
                    id="allocation-price"
                    name="allocation-price"
                    defaultValue={customAllocationAmount}
                    decimalsLimit={2}
                    decimalScale={2}
                    className="allocation-price-input"
                    value={customAllocationAmount}
                    allowDecimals={true}
                    onValueChange={handleAllocationAmountChange}
                  />
                  <div className="percentage">
                    = {calcVolume() > 100.0 ? "--" : `${calcVolume()}%`}
                  </div>
                </div>
              </div>

              <div className="divider"></div>
              {!isEditAllocation && errorMessage && (
                <div className="error-msg">{errorMessage}</div>
              )}
            </>
          )}
        </div>
      )}
      {allocationError && <p className="recommended">{allocationError}</p>}
      <div className="btn-continue">
        <Button
          className="wt-btn-allocation"
          disabled={
            (isEditAllocation && !Object.entries(strategies)?.length) ||
            errorMessage !== "" ||
            isSubmitting ||
            isLoading ||
            props.minimumInvestmentLoading
          }
          onClick={handleContinue}
          width="100%"
        >
          {isSubmitting ? <Loader /> : getLabel()}
        </Button>
      </div>
    </AllocationAmountModalContainer>
  );
};

AllocationAmountModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onContinue: PropTypes.func,
  handleBack: PropTypes.func,
  showBackButton: PropTypes.bool,
  accountId: PropTypes.string,
  subscriptionId: PropTypes.string,
  isEditAllocation: PropTypes.bool,
  strategyName: PropTypes.string,
  minimumInvestment: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  minimumInvestmentLoading: PropTypes.bool,
  portfolioName: PropTypes.string,
  portfolioLogo: PropTypes.any,
  portfolioKey: PropTypes.string,
  isEditStrategies: PropTypes.bool,
  portFolioData: PropTypes.any,
  showClose: PropTypes.bool,
};
